import React, { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';
import Step7 from './Step7';
import EditorialShell from './EditorialShell';

function placeholderImageDataUri(label: string): string {
  const safeLabel = label.replace(/[<>&"]/g, '');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="#f5f5fa" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#646478" font-family="system-ui,sans-serif" font-size="16">${safeLabel}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const SimpleApp: React.FC = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [superpossibleKey, setSuperpossibleKey] = useState('');
  
  // Form data
  const [userName, setUserName] = useState('');
  const [category, setCategory] = useState('');
  const [productShortDescription, setProductShortDescription] = useState('');
  const [productNames, setProductNames] = useState<string[]>([]);
  const [selectedName, setSelectedName] = useState('');
  const [isGeneratingNames, setIsGeneratingNames] = useState(false);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [editedDescriptions, setEditedDescriptions] = useState<string[]>(['', '']);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [selectedDescriptionIndex, setSelectedDescriptionIndex] = useState<number>(-1);
  const [isGeneratingDescriptions, setIsGeneratingDescriptions] = useState(false);
  const [pricing, setPricing] = useState<{min: number, max: number, recommended: number}>({min: 0, max: 0, recommended: 0});
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [isGeneratingPricing, setIsGeneratingPricing] = useState(false);
  const [shopifyUrl, setShopifyUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  // Every generated artefact records a signature of the answers it was made
  // from. When the user steps back and edits one of those answers the
  // signature stops matching, and Continue regenerates instead of carrying
  // stale work forward.
  const [namesSignature, setNamesSignature] = useState('');
  const [imagesSignature, setImagesSignature] = useState('');
  const [descriptionsSignature, setDescriptionsSignature] = useState('');
  const [pricingSignature, setPricingSignature] = useState('');

  // Bumped whenever a stage is invalidated, so a superseded request cannot
  // overwrite newer results when it finally resolves.
  const namesRun = useRef(0);
  const imagesRun = useRef(0);
  const descriptionsRun = useRef(0);
  const pricingRun = useRef(0);

  React.useEffect(() => {
    const savedSuperpossibleKey = localStorage.getItem('superpossible_key');
    if (savedSuperpossibleKey) {
      setSuperpossibleKey(savedSuperpossibleKey);
    }
  }, []);


  const processStep = async (data: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/process-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, superpossible_key: superpossibleKey, ...data })
      });
      
      const result = await response.json();
      
      if (!sessionId) setSessionId(result.session_id);
      
      return result;
    } catch (error) {
      console.error('API Error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1 = async () => {
    if (!userName.trim()) return;
    await processStep({ user_name: userName });
    setStep(2);
  };

  // Signatures of the answers each stage depends on.
  const briefSignature = (description: string = productShortDescription) =>
    `${category}::${description.trim()}`;
  const nameSignature = () => `${briefSignature()}::${selectedName}`;
  const imageSignature = () => `${nameSignature()}::${[...selectedImages].sort().join('|')}`;
  const descriptionSignature = () => `${imageSignature()}::${selectedDescription}`;

  // Invalidating a stage cascades to everything derived from it.
  const clearPricing = () => {
    pricingRun.current += 1;
    setPricing({ min: 0, max: 0, recommended: 0 });
    setSelectedPrice(0);
    setPricingSignature('');
    setIsGeneratingPricing(false);
  };

  const clearDescriptions = () => {
    descriptionsRun.current += 1;
    setDescriptions([]);
    setEditedDescriptions(['', '']);
    setSelectedDescription('');
    setSelectedDescriptionIndex(-1);
    setDescriptionsSignature('');
    setIsGeneratingDescriptions(false);
    clearPricing();
  };

  const clearImages = () => {
    imagesRun.current += 1;
    setProductImages([]);
    setSelectedImages([]);
    setImagesSignature('');
    setIsGeneratingImages(false);
    clearDescriptions();
  };

  const clearNames = () => {
    namesRun.current += 1;
    setProductNames([]);
    setSelectedName('');
    setNamesSignature('');
    setIsGeneratingNames(false);
    clearImages();
  };

  const handleStep2 = async (customDescription?: string) => {
    if (!category) return;
    const description =
      customDescription !== undefined ? customDescription : productShortDescription;
    const signature = briefSignature(description);

    setStep(3);

    // Already generating this exact brief, or already have its results.
    if (namesSignature === signature && (isGeneratingNames || productNames.length > 0)) return;

    clearNames();
    const runId = namesRun.current;
    setNamesSignature(signature);
    setIsGeneratingNames(true);

    const fallback = [`Amazing ${category}`, `Premium ${category}`, `Super ${category}`];

    try {
      const result = await processStep({
        product_category: category,
        product_short_description: description,
      });
      if (namesRun.current !== runId) return;
      const names = (result?.product_names || []).filter(
        (name: string) => name && !/^default ai response$/i.test(name.trim())
      );
      setProductNames(names.length >= 3 ? names : fallback);
    } catch (error) {
      console.error('Error generating names:', error);
      if (namesRun.current !== runId) return;
      setProductNames(fallback);
    } finally {
      if (namesRun.current === runId) setIsGeneratingNames(false);
    }
  };

  const handleStep3 = async () => {
    if (!selectedName) return;
    const signature = nameSignature();

    setStep(4);

    processStep({ product_name: selectedName }).catch((error) => {
      console.error('Error saving product name:', error);
    });

    // Already shooting this exact product, or already have the shots.
    const isFresh = productImages.length > 0 && productImages.every(Boolean);
    if (imagesSignature === signature && (isGeneratingImages || isFresh)) return;

    clearImages();
    const runId = imagesRun.current;
    setImagesSignature(signature);
    setIsGeneratingImages(true);

    generateImagesIndividually(runId).catch((error) => {
      console.error('Error generating images:', error);
      if (imagesRun.current === runId) setIsGeneratingImages(false);
    });
  };

  const generateImagesIndividually = async (runId: number) => {
    const isCurrent = () => imagesRun.current === runId;
    const imagePrompts = [
      `Professional product photo of ${selectedName}, ${category}, ${productShortDescription}, clean white background, high quality, commercial photography`,
      `${selectedName} ${category} ${productShortDescription} product, lifestyle photo, modern setting, natural lighting, premium quality`,
      `Minimalist ${selectedName} ${category} ${productShortDescription}, artistic composition, soft lighting, elegant presentation`,
      `Creative ${selectedName} ${category} ${productShortDescription} showcase, dynamic angle, professional studio lighting`
    ];

    // Pre-populate with empty strings so the grid shows loading slots.
    setProductImages(['', '', '', '']);
    
    try {
      // Create promises for each image but don't wait for all of them
      const imagePromises = imagePrompts.map(async (prompt, index) => {
        try {
          console.log(`Starting image generation ${index + 1}...`);
          const response = await fetch(`${API_URL}/api/generate-single-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              session_id: sessionId,
              superpossible_key: superpossibleKey,
              product_name: selectedName,
              product_category: category,
              product_short_description: productShortDescription,
              index: index
            })
          });
          
          const result = await response.json();
          console.log(`Image ${index + 1} completed:`, result);

          if (!isCurrent()) return { index, image_url: '' };

          // Update the images array immediately when this image is ready
          setProductImages(prevImages => {
            const newImages = [...prevImages];
            newImages[index] = result.image_url;
            return newImages;
          });
          
          return { index, image_url: result.image_url };
        } catch (error) {
          console.error(`Error generating image ${index + 1}:`, error);
          const fallbackUrl = placeholderImageDataUri(`Error ${index + 1}`);

          if (!isCurrent()) return { index, image_url: '' };

          // Update with fallback image
          setProductImages(prevImages => {
            const newImages = [...prevImages];
            newImages[index] = fallbackUrl;
            return newImages;
          });
          
          return { index, image_url: fallbackUrl };
        }
      });

      // Wait for all images to complete (but UI updates happen immediately)
      await Promise.allSettled(imagePromises);
      
    } catch (error) {
      console.error('Error generating images:', error);
      if (!isCurrent()) return;
      // Fallback images
      setProductImages([
        placeholderImageDataUri(`${selectedName} 1`),
        placeholderImageDataUri(`${selectedName} 2`),
        placeholderImageDataUri(`${selectedName} 3`),
        placeholderImageDataUri(`${selectedName} 4`),
      ]);
    } finally {
      if (isCurrent()) setIsGeneratingImages(false);
    }
  };

  const handleStep4 = async () => {
    if (selectedImages.length === 0) return;
    const signature = imageSignature();

    setStep(5);

    processStep({
      selected_images: selectedImages,
      selected_image: selectedImages[0], // Keep backward compatibility
    }).catch((error) => {
      console.error('Error saving selected images:', error);
    });

    const isFresh = descriptions.length > 0 && descriptions.every(Boolean);
    if (descriptionsSignature === signature && (isGeneratingDescriptions || isFresh)) return;

    clearDescriptions();
    const runId = descriptionsRun.current;
    setDescriptionsSignature(signature);
    setIsGeneratingDescriptions(true);

    generateDescriptionsIndividually(runId).catch((error) => {
      console.error('Error generating descriptions:', error);
      if (descriptionsRun.current === runId) setIsGeneratingDescriptions(false);
    });
  };

  const generateDescriptionsIndividually = async (runId: number) => {
    const isCurrent = () => descriptionsRun.current === runId;
    const primaryImage = selectedImages[0];

    // Pre-populate with empty strings so the grid shows loading slots.
    setDescriptions(['', '']);
    
    try {
      // Create 2 concurrent API calls for individual description generation
      const descriptionPromises = [0, 1].map(async (index) => {
        try {
          console.log(`Starting description generation ${index + 1}...`);
          const response = await fetch(`${API_URL}/api/generate-single-description`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              session_id: sessionId,
              superpossible_key: superpossibleKey,
              selected_image: primaryImage,
              product_name: selectedName,
              product_category: category,
              product_short_description: productShortDescription,
              index: index,
              type: index === 0 ? 'modern_and_aspirational' : 'playful_and_purposeful'
            })
          });
          
          const result = await response.json();
          console.log(`Description ${index + 1} completed:`, result);

          if (!isCurrent()) return { index, description: '' };

          // Update the descriptions array immediately when this description is ready
          setDescriptions(prevDescriptions => {
            const newDescriptions = [...prevDescriptions];
            newDescriptions[index] = result.description;
            return newDescriptions;
          });
          
          return { index, description: result.description };
        } catch (error) {
          console.error(`Error generating description ${index + 1}:`, error);
          const fallbackDesc = `${selectedName} - A premium ${category.toLowerCase()} with exceptional quality and design. Perfect for ${index === 0 ? 'everyday use' : 'making a statement'}.`;

          if (!isCurrent()) return { index, description: '' };

          // Update with fallback description
          setDescriptions(prevDescriptions => {
            const newDescriptions = [...prevDescriptions];
            newDescriptions[index] = fallbackDesc;
            return newDescriptions;
          });
          
          return { index, description: fallbackDesc };
        }
      });

      // Wait for all descriptions to complete (but UI updates happen immediately)
      await Promise.allSettled(descriptionPromises);
      
    } catch (error) {
      console.error('Error generating descriptions:', error);
      if (!isCurrent()) return;
      // Fallback descriptions
      setDescriptions([
        `${selectedName} - A premium ${category.toLowerCase()} with exceptional quality and design. Perfect for everyday use with outstanding features.`,
        `${selectedName} - Express your unique style with this stunning ${category.toLowerCase()}. Crafted to inspire and impress wherever you go.`
      ]);
    } finally {
      if (isCurrent()) setIsGeneratingDescriptions(false);
    }
  };

  const handleStep5 = async () => {
    if (!selectedDescription) return;
    const signature = descriptionSignature();

    setStep(6);

    if (pricingSignature === signature && (isGeneratingPricing || pricing.recommended > 0)) return;

    clearPricing();
    const runId = pricingRun.current;
    setPricingSignature(signature);
    setIsGeneratingPricing(true);

    const fallback = { min: 15, max: 50, recommended: 25 };

    try {
      const result = await processStep({
        product_description: selectedDescription,
        selected_images: selectedImages, // Make sure selected images are also sent
      });
      if (pricingRun.current !== runId) return;
      const next = result?.pricing ?? fallback;
      setPricing(next);
      setSelectedPrice(next.recommended);
    } catch (error) {
      console.error('Error generating pricing:', error);
      if (pricingRun.current !== runId) return;
      setPricing(fallback);
      setSelectedPrice(fallback.recommended);
    } finally {
      if (pricingRun.current === runId) setIsGeneratingPricing(false);
    }
  };

  const handleStep6 = async () => {
    if (!selectedPrice) return;
    const result = await processStep({ selected_price: selectedPrice });
    if (result?.shopify_url) {
      setShopifyUrl(result.shopify_url);
      if (result?.preview_url) {
        setPreviewUrl(result.preview_url);
      }
      setStep(7);
    } else {
      toast.error('Publishing to Shopify failed. Please try again.');
    }
  };

  const categories = ['Health & Wellness', "Women's Fashion", "Men's Fashion", 'Electronics', 'Packaged Food'];

  const stepLabels = [
    { label: 'Introduction' },
    { label: 'The Brief' },
    { label: 'Naming' },
    { label: 'Photography' },
    { label: 'Copy' },
    { label: 'Pricing' },
    { label: 'Published' },
  ];

  // Check if current step can proceed
  const canProceed = () => {
    switch (step) {
      case 1: return userName.trim() !== '';
      case 2: return category !== '' && productShortDescription.trim() !== '';
      case 3: return selectedName !== '' && selectedName !== 'custom' && !isGeneratingNames;
      case 4: return selectedImages.length > 0 && !isGeneratingImages;
      case 5: return selectedDescription !== '' && !isGeneratingDescriptions;
      case 6: return selectedPrice > 0 && !isGeneratingPricing;
      default: return false;
    }
  };

  // Get current step handler
  const getCurrentStepHandler = () => {
    switch (step) {
      case 1: return handleStep1;
      case 2: return () => {
        // For step 2, we need to ensure the latest productShortDescription is used
        handleStep2(productShortDescription);
      };
      case 3: return handleStep3;
      case 4: return handleStep4;
      case 5: return handleStep5;
      case 6: return handleStep6;
      default: return () => {};
    }
  };

  // A short line of guidance sitting in the action bar, so the step content
  // itself stays free of instructional chrome.
  const actionNote = () => {
    switch (step) {
      case 1:
        return userName.trim() ? 'Ready when you are.' : 'Enter your name to begin.';
      case 2:
        if (!category) return 'Choose a category.';
        return productShortDescription.trim() ? 'Ready when you are.' : 'Describe the product.';
      case 3:
        if (isGeneratingNames) return 'Writing name candidates…';
        return selectedName ? 'Ready when you are.' : 'Choose or write a name.';
      case 4:
        if (isGeneratingImages) return 'Rendering product photography…';
        return selectedImages.length > 0 ? 'Ready when you are.' : 'Select one image.';
      case 5:
        if (isGeneratingDescriptions) return 'Writing product copy…';
        return selectedDescription ? 'Ready when you are.' : 'Select a description.';
      case 6:
        if (isGeneratingPricing) return 'Analysing market pricing…';
        return selectedPrice > 0 ? 'Publishing sends this to Shopify.' : 'Set a price.';
      default:
        return '';
    }
  };

  return (
    <EditorialShell
      studio="product"
      chapter="Product Studio"
      steps={stepLabels}
      currentStep={step}
      onStepSelect={setStep}
      actions={
        step < 7 ? (
          <>
            <button
              type="button"
              className="ed-btn ed-btn--bare"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
            >
              <ArrowLeft size={15} />
              Back
            </button>

            <span className="ed-actionbar-note">{actionNote()}</span>

            <button
              type="button"
              className="ed-btn ed-btn--primary"
              onClick={getCurrentStepHandler()}
              disabled={!canProceed() || loading}
            >
              {loading ? (
                <>
                  <span className="ed-spinner" aria-hidden="true" />
                  Working…
                </>
              ) : (
                <>
                  {step === 6 ? 'Publish' : 'Continue'}
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </>
        ) : undefined
      }
    >
      {step === 1 && (
        <Step1
          userName={userName}
          setUserName={setUserName}
          onEnterPress={getCurrentStepHandler()}
          loading={loading}
        />
      )}

      {step === 2 && (
        <Step2
          category={category}
          setCategory={setCategory}
          handleStep2={handleStep2}
          loading={loading}
          categories={categories}
          productShortDescription={productShortDescription}
          setProductShortDescription={setProductShortDescription}
        />
      )}

      {step === 3 && (
        <Step3
          productNames={productNames}
          selectedName={selectedName}
          setSelectedName={setSelectedName}
          handleStep3={handleStep3}
          loading={loading}
          isGeneratingNames={isGeneratingNames}
        />
      )}

      {step === 4 && (
        <Step4
          productImages={productImages}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          handleStep4={handleStep4}
          loading={loading}
          isGeneratingImages={isGeneratingImages}
        />
      )}

      {step === 5 && (
        <Step5
          descriptions={descriptions}
          editedDescriptions={editedDescriptions}
          setEditedDescriptions={setEditedDescriptions}
          selectedDescription={selectedDescription}
          setSelectedDescription={setSelectedDescription}
          selectedDescriptionIndex={selectedDescriptionIndex}
          setSelectedDescriptionIndex={setSelectedDescriptionIndex}
          handleStep5={handleStep5}
          loading={loading}
          isGeneratingDescriptions={isGeneratingDescriptions}
        />
      )}

      {step === 6 && (
        <Step6
          pricing={pricing}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
          handleStep6={handleStep6}
          loading={loading}
          isGeneratingPricing={isGeneratingPricing}
        />
      )}

      {step === 7 && (
        <Step7
          selectedName={selectedName}
          selectedPrice={selectedPrice}
          category={category}
          shopifyUrl={shopifyUrl}
          previewUrl={previewUrl}
          productImage={selectedImages.length > 0 ? selectedImages[0] : ''}
        />
      )}
    </EditorialShell>
  );
};

export default SimpleApp; 